import { CLAIM_CATEGORY, TRIP_REASON } from "../constants/claimConstants";
import {
  CLAIM_TYPE,
  isOwnVehicleMode,
  isYearlyFrequency,
} from "../constants/conveyanceConstants";



/** Local-time `yyyy-MM-dd`, which is what the DTOs' @JsonFormat expects. */
export function toIsoDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseIsoDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** claim_month is stored normalised to the first of the month. */
export function firstOfMonth(value) {
  const date = parseIsoDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function monthKeyOf(value) {
  const date = parseIsoDate(value);
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function sameCalendarMonth(a, b) {
  const left = monthKeyOf(a);
  const right = monthKeyOf(b);
  return Boolean(left) && left === right;
}

/**
 * Indian financial year window key, 1 Apr - 31 Mar, e.g. "FY2026-27".
 * Nine characters, inside maintenance_frequency_window's VARCHAR(12).
 */
export function financialYearKeyOf(value) {
  const date = parseIsoDate(value);
  if (!date) return "";
  const startYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `FY${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
}

/**
 * org_expense_claim.maintenance_frequency_window — which window a maintenance
 * claim falls in, per the policy's configured frequency. The uniqueness rule
 * (one claim per vehicle type per window) is enforced against this key.
 */
export function maintenanceWindowKey(value, frequency) {
  return isYearlyFrequency(frequency) ? financialYearKeyOf(value) : monthKeyOf(value);
}

/**
 * The date range a trip line may fall in: the previous month and the current
 * one, up to today.
 *
 * The deadline day no longer closes this window. Missing it no longer blocks a
 * claim — it makes the submission LATE, which the backend records as
 * Late_submitted against a mandatory reason. Refusing the date here would
 * leave an employee unable to file the very claim the rule exists to allow, so
 * the calendar stays open and lateness is declared instead (see
 * isLateSubmission).
 *
 * Future dates are still refused, and so is anything older than last month —
 * that has no path through this form.
 */
export function tripDateBounds({ today = new Date() } = {}) {
  return {
    min: new Date(today.getFullYear(), today.getMonth() - 1, 1),
    max: new Date(today.getFullYear(), today.getMonth(), today.getDate()),
  };
}

/**
 * Whether submitting a claim for `claimMonth` today counts as late.
 *
 * A claim for the CURRENT month is never late: its deadline is next month's
 * day, which has not arrived. A claim for any earlier month is late once the
 * current month has passed its deadline day — so an August claim filed on
 * 5 September is on time, and the same claim on 6 September is not.
 *
 * This mirrors the backend rule so the form can ask for the reason the backend
 * will require; the backend remains the authority on the status it stores.
 */
export function isLateSubmission(claimMonth, { deadlineDay, today = new Date() } = {}) {
  const month = monthKeyOf(claimMonth);
  if (!month) return false;
  if (month >= monthKeyOf(today)) return false;

  // Number(null) and Number("") are 0, not NaN — without the lower bound a
  // policy that configures no deadline day would make every past-month claim
  // late.
  const day = Number(deadlineDay);
  if (!Number.isFinite(day) || day <= 0) return false;
  return today.getDate() > day;
}

export function isTripDateAllowed(value, bounds) {
  const date = parseIsoDate(value);
  if (!date || !bounds) return false;
  return date >= bounds.min && date <= bounds.max;
}

/* ------------------------------------------------------------- policy view */

/** The policy's rate for a mode, or null for the at-actuals modes. */
export function rateForMode(modes, mode) {
  if (!isOwnVehicleMode(mode)) return null;
  const row = (modes || []).find((entry) => entry.modeTransport === mode);
  const rate = Number(row?.ratePerKm);
  return Number.isFinite(rate) ? rate : null;
}
/**
 * The id of the policy mode row a trip was taken under.
 *
 * Resolved for every mode, not just the own-vehicle ones: each enabled mode is
 * a row on the active policy, so the line points at the row it was priced
 * against, whether or not that row carries a rate.
 */
export function modeIdFor(modes, mode) {
  const row = (modes || []).find((entry) => entry.modeTransport === mode);
  return row?.modeId ?? null;
}

/**
 * The reverse of modeIdFor: the mode string a stored line was taken under,
 * resolved from its policy-mode id. The line persists mode_id (an fk), not the
 * label, so reopening a claim for edit has to map that id back to the mode the
 * grid works in. Falls back to null when the id is not among the active modes.
 */
export function modeTransportForId(modes, modeId) {
  if (modeId == null) return null;
  const row = (modes || []).find((entry) => String(entry.modeId) === String(modeId));
  return row?.modeTransport ?? null;
}


/** Only modes the policy has switched on may appear in the Mode dropdown. */
export function enabledModes(modes) {
  return (modes || []).filter((mode) => mode.isEnabled).map((mode) => mode.modeTransport);
}

/**
 * Which New Claim categories the active policy permits:
 *   - the Claim Type toggle picks exactly one of per-km / fixed monthly
 *   - Vehicle Maintenance is independent of that toggle
 */
export function availableCategories(policy) {
  if (!policy) return [];
  const categories = [];
  if (policy.claimType === CLAIM_TYPE.FIXED_MONTHLY) {
    categories.push(CLAIM_CATEGORY.FIXED_MONTHLY);
  } else if (policy.claimType === CLAIM_TYPE.RATE_PER_KM) {
    categories.push(CLAIM_CATEGORY.RATE_PER_KM);
  }
  if (policy.maintenanceSupported) categories.push(CLAIM_CATEGORY.MAINTENANCE);
  return categories;
}

/** The policy cap for the selected vehicle type — separate per type, never shared. */
export function maintenanceCapFor(policy, vehicleType) {
  if (!policy?.maintenanceSupported || !vehicleType) return null;
  const raw =
    vehicleType === "Own_Car" ? policy.maintenanceMaxCar : policy.maintenanceMaxBike;
  const cap = Number(raw);
  return Number.isFinite(cap) && cap > 0 ? cap : null;
}

/* --------------------------------------------------------------- money */

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function round2(value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

/**
 * Parking and toll only exist on an own-vehicle line, and only while the
 * policy covers them. When hidden they are excluded from the sum entirely —
 * treated as zero, not left blank.
 */
export function tollsParkingVisible(line, policy) {
  return Boolean(policy?.tollsParkingCovered) && isOwnVehicleMode(line?.mode);
}

/**
 * Own Bike / Own Car: distance x the policy rate, computed and read-only.
 * Every other mode is a manual at-actuals amount.
 */
export function computeLineAmount(line, modes) {
  if (!isOwnVehicleMode(line?.mode)) return round2(line?.amount);
  const rate = rateForMode(modes, line.mode);
  if (rate === null) return 0;
  return round2(toNumber(line.distanceKm) * rate);
}

export function computeLineTotal(line, modes, policy) {
  const amount = computeLineAmount(line, modes);
  if (!tollsParkingVisible(line, policy)) return round2(amount);
  return round2(amount + toNumber(line.parkingCharges) + toNumber(line.tollCharges));
}

export function computeClaimTotal(lines, modes, policy) {
  return round2(
    (lines || []).reduce((sum, line) => sum + computeLineTotal(line, modes, policy), 0)
  );
}

/**
 * Proof is mandatory for an at-actuals mode, and for any line carrying a
 * non-zero parking or toll value. Own-vehicle distance with no toll/parking
 * needs none — there is no receipt for your own mileage.
 */
export function proofRequiredForLine(line, policy) {
  if (!line?.mode) return false;
  if (!isOwnVehicleMode(line.mode)) return true;
  if (!tollsParkingVisible(line, policy)) return false;
  return toNumber(line.parkingCharges) > 0 || toNumber(line.tollCharges) > 0;
}

/* ---------------------------------------------------------------- proofs */

/**
 * A claim carries every proof itself, in its `attachments` list — there is no
 * proof endpoint to call. Each entry is
 *
 *   { fileName, fileByte, contentType, fileSize, claimLineId }
 *
 * where fileByte is bare Base64 (no data: prefix) and claimLineId names the
 * trip line a per-km proof backs; it is null for a proof on the claim as a
 * whole (fixed monthly / maintenance). fileSize and claimLineId are set by the
 * server, so a request only sends fileName, fileByte and contentType — a per-km
 * line sends its proofs inside its own lineDetail entry.
 *
 * The forms edit proofs as a mix of Files just picked and stored attachments
 * rehydrated as { name, data, contentType, size }, where `data` is a ready
 * data: URL; everything below converts between the two shapes.
 */
const BASE64_MIME_SIGNATURES = [
  ["JVBER", "application/pdf"],
  ["iVBOR", "image/png"],
  ["/9j/", "image/jpeg"],
  ["R0lGOD", "image/gif"],
  ["UklGR", "image/webp"],
];

const PROOF_EXTENSION = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

/** The MIME type of a stored document, from its data: URL or its signature. */
export function proofMimeType(raw) {
  const value = String(raw || "");
  const declared = value.match(/^data:([^;,]+)[;,]/);
  if (declared) return declared[1];
  const guessed = BASE64_MIME_SIGNATURES.find(([signature]) => value.startsWith(signature));
  return guessed ? guessed[1] : "application/octet-stream";
}

/** A data: URL an <img>, <object> or a download link can use directly. */
export function proofDataUrl(raw) {
  const value = String(raw || "");
  if (!value) return "";
  if (!value.startsWith("data:")) return `data:${proofMimeType(value)};base64,${value}`;
  return value;
}

/** A stored attachment's MIME type — as stored, or guessed from its bytes. */
function attachmentMimeType(attachment) {
  return attachment?.contentType || proofMimeType(attachment?.fileByte);
}

/**
 * What to call a stored attachment: the name it was uploaded under. Only an
 * entry with none falls back to `base` plus the extension its content implies,
 * which is enough for a download to open in the right application.
 */
function attachmentFileName(attachment, base = "proof") {
  if (attachment?.fileName) return attachment.fileName;
  const extension = PROOF_EXTENSION[attachmentMimeType(attachment)];
  return extension ? `${base}.${extension}` : base;
}

/** Whether a stored attachment belongs to this trip line (null = the claim itself). */
function belongsTo(attachment, claimLineId) {
  const owner = attachment?.claimLineId ?? null;
  const wanted = claimLineId ?? null;
  return owner === null || wanted === null ? owner === wanted : String(owner) === String(wanted);
}

/** A stored attachment in the shape ProofCell / ProofPreviewPanel render. */
export function storedProof(attachment, { proofId, fileNameBase } = {}) {
  if (!attachment?.fileByte) return null;
  const mimeType = attachmentMimeType(attachment);
  return {
    proofId,
    claimLineId: attachment.claimLineId ?? null,
    url: `data:${mimeType};base64,${attachment.fileByte}`,
    fileName: attachmentFileName(attachment, fileNameBase),
    mimeType,
    fileSize: attachment.fileSize ?? null,
  };
}

/** A stored attachment in the shape the claim forms edit (attach / download / remove). */
export function attachmentFromStored(attachment) {
  if (!attachment?.fileByte) return null;
  const contentType = attachmentMimeType(attachment);
  return {
    name: attachmentFileName(attachment),
    data: `data:${contentType};base64,${attachment.fileByte}`,
    contentType,
    size: attachment.fileSize ?? null,
  };
}

/**
 * The stored attachments of one trip line, or of the claim as a whole when
 * `claimLineId` is null, in the shape the claim forms edit.
 */
export function attachmentsFromStored(attachments, claimLineId = null) {
  return (attachments || [])
    .filter((attachment) => belongsTo(attachment, claimLineId))
    .map(attachmentFromStored)
    .filter(Boolean);
}

/* ----------------------------------------------------------- validation */

/**
 * Full per-km submit validation. Returns one entry per failing line so the
 * form can render the line-by-line summary the PRD asks for; `field` lets the
 * row highlight the offending input.
 *
 * Draft saves skip this entirely — an incomplete claim is allowed to rest.
 */
export function validateTripLines(lines, { modes, policy, bounds } = {}) {
  const problems = [];
  const rows = lines || [];

  const push = (index, field, messageKey, values) =>
    problems.push({ lineNo: index + 1, field, messageKey, values });

  if (rows.length === 0) {
    problems.push({ lineNo: null, field: "lines", messageKey: "conveyanceClaim.errorAtLeastOneLine" });
    return problems;
  }

  const claimMonth = rows.find((line) => line.tripDate)?.tripDate ?? null;

  rows.forEach((line, index) => {
    if (!line.tripDate) {
      push(index, "tripDate", "conveyanceClaim.errorDateRequired");
    } else {
      if (bounds && !isTripDateAllowed(line.tripDate, bounds)) {
        push(index, "tripDate", "conveyanceClaim.errorDateOutOfWindow");
      }
      if (claimMonth && !sameCalendarMonth(line.tripDate, claimMonth)) {
        push(index, "tripDate", "conveyanceClaim.errorDateDifferentMonth");
      }
    }

    if (!line.reason) push(index, "reason", "conveyanceClaim.errorReasonRequired");

    if (line.reason === TRIP_REASON.CUSTOMER_SITE_VISIT) {
      if (!line.customerId) push(index, "customerId", "conveyanceClaim.errorCustomerRequired");
      if (!line.areaLocationId) push(index, "areaLocationId", "conveyanceClaim.errorAreaRequired");
    } else if (line.reason) {
      if (!String(line.description || "").trim()) {
        push(index, "description", "conveyanceClaim.errorDescriptionRequired");
      }
    }

    if (!String(line.fromLocation || "").trim()) {
      push(index, "fromLocation", "conveyanceClaim.errorFromRequired");
    }
    if (!String(line.toLocation || "").trim()) {
      push(index, "toLocation", "conveyanceClaim.errorToRequired");
    }

    if (!line.mode) {
      push(index, "mode", "conveyanceClaim.errorModeRequired");
    } else if (isOwnVehicleMode(line.mode)) {
      if (toNumber(line.distanceKm) <= 0) {
        push(index, "distanceKm", "conveyanceClaim.errorDistanceGreaterThanZero");
      }
      if (rateForMode(modes, line.mode) === null) {
        push(index, "mode", "conveyanceClaim.errorNoRateConfigured");
      }
    } else if (toNumber(line.amount) <= 0) {
      push(index, "amount", "conveyanceClaim.errorAmountGreaterThanZero");
    }

    if (proofRequiredForLine(line, policy) && !(line.proofs || []).length) {
      push(index, "proofs", "conveyanceClaim.errorProofRequired");
    }
  });

  return problems;
}

/* --------------------------------------------------------- server errors */

/**
 * The backend rejects a second claim in the same frequency window with
 * DUPLICATE_ENTRY and a `data.window` key, e.g. "2026-08" or "FY2026-27".
 * Its raw message names the category enum, so the screens re-word it — and the
 * wording depends on the POLICY, since the window is a calendar month or an
 * Indian financial year depending on how HR configured the frequency.
 *
 * Returns an i18n key plus values, so this stays free of React and i18n.
 */
export function duplicateWindowError(error, { yearly = false, vehicle } = {}) {
  if (error?.errorCode !== "DUPLICATE_ENTRY") return null;

  const window = error?.data?.window ?? "";

  // Maintenance windows are per vehicle type: "a claim already exists" reads
  // as a contradiction to someone who has never filed for this vehicle, so the
  // message names the one that actually clashed.
  if (vehicle) {
    return {
      messageKey: yearly
        ? "conveyanceClaim.errorDuplicateMaintenanceYearly"
        : "conveyanceClaim.errorDuplicateMaintenanceMonthly",
      values: { window, vehicle },
    };
  }

  return {
    messageKey: yearly
      ? "conveyanceClaim.errorDuplicateYearly"
      : "conveyanceClaim.errorDuplicateMonthly",
    values: { window },
  };
}

/* ------------------------------------------------------- line persistence */

/**
 * One entry of the claim's `lineDetail` array.
 *
 * The DTO's names are not the form's names, and this is the one place the two
 * are reconciled — the grid keeps working in its own vocabulary:
 *
 *   form            request
 *   reason      ->  tripReason
 *   mode        ->  modeTransport
 *   description ->  claimDescription
 *   line total  ->  totalAmount     (amount + parking + toll)
 *   proofs      ->  attachments     (every file; replaces the line's stored set)
 *
 * Two fields are snapshots taken at save time, not live values:
 *   - ratePerKm, so a later policy rate change never re-prices this trip
 *   - totalAmount, so the stored figure matches what the submitter saw, and
 *     the line totals sum to the claim header's own totalAmount
 *
 * There is no line number: position in `lineDetail` is the line's order. And
 * no claimId, because the line is saved as part of the claim that owns it.
 *
 * Async because the proofs are encoded here — see encodeAttachments.
 */
export async function tripLinePayload(line, { modes, policy, metricId, claimId }) {
  const ownVehicle = isOwnVehicleMode(line.mode);
  const showTollsParking = tollsParkingVisible(line, policy);

  return {
    // Null while the claim is being created — it has no id to point at yet.
    claimId: claimId ?? null,
    claimLineId: line.claimLineId ?? null,
    tripDate: line.tripDate || null,
    tripReason: line.reason || null,
    customerId: line.customerId ? Number(line.customerId) : null,
    claimDescription: line.description || null,
    fromLocation: line.fromLocation || null,
    toLocation: line.toLocation || null,
    areaLocationId: line.areaLocationId ? Number(line.areaLocationId) : null,
    modeId: modeIdFor(modes, line.mode),
    modeTransport: line.mode || null,
    distanceKm: ownVehicle ? round2(line.distanceKm) : null,
    ratePerKm: ownVehicle ? rateForMode(modes, line.mode) : null,
    parkingCharges: showTollsParking ? round2(line.parkingCharges) : null,
    tollCharges: showTollsParking ? round2(line.tollCharges) : null,
    
    metricId: metricId ?? null,
    totalAmount: computeLineTotal(line, modes, policy),
    attachments: await encodeAttachments(line.proofs),
  };
}

/** One proof as the API wants it: bare Base64 with its name and type beside it. */
function toAttachment(dataUrl, name, type) {
  const match = String(dataUrl || "").match(/^data:([^;,]*)[^,]*,([\s\S]*)$/);
  const fileByte = match ? match[2] : "";
  if (!fileByte) return null;
  return {
    fileName: name || "proof",
    fileByte,
    contentType: type || match[1] || proofMimeType(fileByte),
  };
}

function encodeAttachment(proof) {
  if (!proof) return Promise.resolve(null);
  // Rehydrated from a saved claim: its bytes go back as they came, so
  // re-saving an untouched claim keeps the proof.
  if (typeof proof.data === "string") {
    return Promise.resolve(toAttachment(proof.data, proof.name, proof.contentType));
  }
  if (typeof FileReader === "undefined" || !(proof instanceof Blob)) return Promise.resolve(null);

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(toAttachment(reader.result, proof.name, proof.type));
    // A receipt that will not encode must not take the whole claim down with
    // it: the claim saves without it and the proofs rule flags it next time.
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(proof);
  });
}

/**
 * Every proof in a form's list, as the payload's `attachments` wants them.
 *
 * The list is the complete set: the backend replaces what it stores with it, so
 * a proof the user removed is simply absent and an empty list clears them all.
 */
export async function encodeAttachments(proofs) {
  const encoded = await Promise.all((proofs || []).map(encodeAttachment));
  return encoded.filter(Boolean);
}

/**
 * A stored row back into the shape the grid edits.
 *
 * `modes` (the active policy's mode rows) is needed to turn the line's stored
 * mode_id back into the mode string the grid works in — the line persists the
 * id, not the label. It falls back to any modeTransport the API echoes, then to
 * blank, so a line still opens even before the policy modes have loaded.
 *
 * `attachments` is the CLAIM's attachments list: a line's proofs are stored on
 * the claim, tagged with its claimLineId, and picked out here.
 */
export function tripLineFromRow(row, modes, attachments) {
  return {
    key: `line-${row.claimLineId}`,
    claimLineId: row.claimLineId,
    tripDate: row.tripDate ? String(row.tripDate).slice(0, 10) : "",
    reason: row.tripReason || "",
    customerId: row.customerId ?? "",
    description: row.claimDescription ?? "",
    fromLocation: row.fromLocation ?? "",
    toLocation: row.toLocation ?? "",
    areaLocationId: row.areaLocationId ?? "",
    mode: modeTransportForId(modes, row.modeId) || row.modeTransport || "",
    // Kept so the mode can still be resolved if the policy modes arrive after
    // the claim was mapped (see the late-resolution effect on the page).
    modeId: row.modeId ?? null,
    distanceKm: row.distanceKm ?? "",
    // A stored line carries one money field, totalAmount. An at-actuals line
    // has no parking or toll, so that IS its amount; an own-vehicle line
    // recomputes its own from distance x rate and ignores this.
    amount: row.totalAmount ?? "",
    parkingCharges: row.parkingCharges ?? "",
    tollCharges: row.tollCharges ?? "",
    proofs: row.claimLineId != null ? attachmentsFromStored(attachments, row.claimLineId) : [],
  };
}

/** A fresh, empty trip line. `mode` is left blank so the policy list drives it. */
export function emptyTripLine() {
  return {
    key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    claimLineId: null,
    tripDate: "",
    reason: "",
    customerId: "",
    description: "",
    fromLocation: "",
    toLocation: "",
    areaLocationId: "",
    mode: "",
    distanceKm: "",
    amount: "",
    parkingCharges: "",
    tollCharges: "",
    proofs: [],
  };
}
